import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import {
  Alert,
  Button,
  Card,
  DataTable,
  Filter,
  InputText,
  RigoFilters,
  type ColumnDef,
} from "@/deps/ui-library";
import { useAccounts, useCalendar, useFiscalYears, useGeneralLedger } from "@/api/queries";
import type { GeneralLedgerLine } from "@/api/types";
import { formatDate, today } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { DateRangeField } from "./DateRangeField";
import { dateShortcuts } from "./glPeriods";
import { ReportFrame } from "./ReportFrame";
import { useDraftFilters } from "./useDraftFilters";
import { VoucherSearch } from "./VoucherSearch";

export interface GeneralLedgerSearch {
  /** The account's code: `/reports/general-ledger?account=1010` opens that ledger from anywhere. */
  account?: string;
  from?: string;
  to?: string;
  q?: string;
  party?: string;
  min?: string;
}

const COLUMNS: Array<ColumnDef<GeneralLedgerLine>> = [
  {
    id: "date",
    header: "Date",
    cell: ({ row: { original: line } }) => (
      <span className="text-fg text-sm whitespace-nowrap">{formatDate(line.date)}</span>
    ),
  },
  {
    id: "document",
    header: "Document",
    cell: ({ row: { original: line } }) => (
      <Link
        to="/doc/$number"
        params={{ number: line.number }}
        className="num text-primary text-sm hover:underline"
      >
        {line.number}
      </Link>
    ),
  },
  {
    id: "reference",
    header: "Reference",
    cell: ({ row: { original: line } }) => (
      <span className="text-fg text-sm">
        {line.reference ?? ""}
        {line.partyName ? <span className="text-fg-muted"> · {line.partyName}</span> : null}
      </span>
    ),
  },
  {
    id: "debit",
    header: "Debit",
    meta: { numeric: true },
    cell: ({ row: { original: line } }) => (Number(line.debit) ? formatAmount(line.debit) : ""),
  },
  {
    id: "credit",
    header: "Credit",
    meta: { numeric: true },
    cell: ({ row: { original: line } }) => (Number(line.credit) ? formatAmount(line.credit) : ""),
  },
  {
    id: "balance",
    header: "Balance",
    meta: { numeric: true },
    cell: ({ row: { original: line } }) => formatAmount(line.balance),
  },
];

/**
 * One account's lines, with its opening rolled forward to the From date.
 *
 * The filters are drafts. The table holds what was applied until Apply, so somebody reading a ledger
 * can set several filters without it reloading under them. A link in applies at once, because the
 * address bar is what's applied.
 */
export function GeneralLedgerPage() {
  const search = useSearch({ strict: false }) as GeneralLedgerSearch;
  const navigate = useNavigate();
  const { data: accounts } = useAccounts();
  const { data: years } = useFiscalYears();
  const calendar = useCalendar();

  const now = today();
  const currentYear =
    years?.find((y) => y.start <= now && now <= y.end) ?? years?.find((y) => y.isCurrent);
  const to = search.to ?? now;
  // A ledger opened at an earlier date starts in that date's year, not this one.
  const year = years?.find((y) => y.start <= to && to <= y.end) ?? currentYear;
  const from = search.from ?? year?.start ?? now;

  const everyAccount = accounts ?? [];
  const usable = everyAccount.filter((a) => a.isActive);
  // Looked for in every account: a trial balance can link to one closed since it last moved.
  const account = everyAccount.find((a) => a.code === search.account) ?? usable[0];

  const { draft, changed, edit, discard } = useDraftFilters({
    account: account?.code ?? "",
    from,
    to,
    q: search.q ?? "",
    party: search.party ?? "",
    min: search.min ?? "",
  });

  const show = (next: GeneralLedgerSearch) =>
    void navigate({ to: "/reports/general-ledger", search: next as never, replace: true });

  // Refused here, because the table would otherwise stay on the last answer under dates nobody asked
  // for while the request fails.
  const refusal =
    draft.from > draft.to
      ? "The period ends before it begins."
      : draft.min.trim() !== "" && !Number.isFinite(Number(draft.min))
        ? "Amount over has to be a number."
        : null;

  const apply = (documentNumber?: string) => {
    if (refusal !== null || (documentNumber === undefined && !changed)) return;
    show({
      account: draft.account || undefined,
      from: draft.from,
      to: draft.to,
      q: (documentNumber ?? draft.q).trim() || undefined,
      party: draft.party || undefined,
      min: draft.min.trim() || undefined,
    });
  };

  const { data, isPending, error } = useGeneralLedger(
    {
      accountId: account?.id ?? "",
      from,
      to,
      q: search.q || undefined,
      partyId: search.party || undefined,
      minAmount: search.min || undefined,
    },
    account !== undefined && years !== undefined,
  );

  // The party list follows the ledger being chosen, before it's applied. Asking without filters
  // means Apply finds this same answer waiting, when no other filter was set.
  const draftAccount = everyAccount.find((a) => a.code === draft.account);
  const otherLedger = draftAccount !== undefined && draftAccount.id !== account?.id;
  const { data: preview } = useGeneralLedger(
    { accountId: draftAccount?.id ?? "", from: draft.from, to: draft.to },
    otherLedger && draft.from <= draft.to,
  );
  const parties = otherLedger
    ? preview?.accountId === draftAccount.id
      ? preview.parties
      : []
    : (data?.parties ?? []);

  const ledgerOptions = usable.map((a) => ({ value: a.code, label: `${a.code} · ${a.name}` }));
  if (account && !usable.includes(account)) {
    ledgerOptions.unshift({ value: account.code, label: `${account.code} · ${account.name}` });
  }
  const partyOptions = parties.map((p) => ({ value: p.id, label: p.name }));

  const filtered = Boolean(search.q || search.party || search.min);
  const clearable =
    changed || filtered || (year !== undefined && from !== year.start) || to !== now;
  const rangeLabel = `${formatDate(from)} – ${formatDate(to)}`;
  const count = data
    ? `${data.matching} ${data.matching === 1 ? "line" : "lines"}${filtered ? ` of ${data.allLines}` : ""}`
    : "";

  return (
    <ReportFrame
      id="gl"
      title="General ledger"
      period={rangeLabel}
      exportable={
        data
          ? () => ({
              title: `General ledger ${data.code} ${data.name}`,
              period: rangeLabel,
              columns: [
                { header: "Date" },
                { header: "Document" },
                { header: "Reference" },
                { header: "Party" },
                { header: "Debit", numeric: true },
                { header: "Credit", numeric: true },
                { header: "Balance", numeric: true },
              ],
              rows: [
                [from, null, "Opening balance", null, null, null, data.opening],
                ...data.lines.map((line) => [
                  line.date,
                  line.number,
                  line.reference,
                  line.partyName,
                  line.debit,
                  line.credit,
                  line.balance,
                ]),
              ],
              totals: [
                to,
                null,
                "Closing balance",
                null,
                data.totalDebit,
                data.totalCredit,
                data.closing,
              ],
            })
          : undefined
      }
    >
      <RigoFilters.Root>
        <RigoFilters.StaticRow>
          <Filter.SingleSelect
            items={ledgerOptions}
            placeholder="Ledger"
            clearable={false}
            value={ledgerOptions.find((option) => option.value === draft.account) ?? null}
            onValueChange={(item) => {
              // A party belongs to the ledger it was listed for.
              if (item) edit({ account: String(item.value), party: "" });
            }}
          />
          <DateRangeField
            from={draft.from}
            to={draft.to}
            shortcuts={dateShortcuts(now, currentYear, calendar)}
            onChange={edit}
          />
          <div className="w-56">
            <VoucherSearch
              value={draft.q}
              accountId={draftAccount?.id}
              from={draft.from}
              to={draft.to}
              onChange={(q) => edit({ q })}
              onSubmit={apply}
            />
          </div>
          {partyOptions.length > 0 ? (
            <Filter.SingleSelect
              items={partyOptions}
              placeholder="All parties"
              searchable={partyOptions.length > 8}
              value={partyOptions.find((option) => option.value === draft.party) ?? null}
              onValueChange={(item) => edit({ party: item ? String(item.value) : "" })}
            />
          ) : null}
          <InputText.Input
            type="text"
            inputMode="decimal"
            aria-label="Amount over"
            placeholder="Amount over"
            config={{ size: "sm" }}
            className="w-32 text-right text-sm"
            value={draft.min}
            onChange={(event) => edit({ min: event.target.value })}
            onKeyDown={(event) => {
              if (event.key === "Enter") apply();
            }}
          />
          <Button
            colorScheme="primary"
            variant="secondary"
            size="sm"
            disabled={!changed || refusal !== null}
            onClick={() => apply()}
          >
            Apply
          </Button>
          {clearable ? (
            <Button
              colorScheme="default"
              variant="ghost"
              size="sm"
              onClick={() => {
                discard();
                show({ account: account?.code });
              }}
            >
              Clear
            </Button>
          ) : null}
          <span className="text-fg-muted ml-auto text-xs">{count}</span>
        </RigoFilters.StaticRow>
      </RigoFilters.Root>

      {refusal ? (
        <p role="alert" className="text-destructive-text text-xs">
          {refusal}
        </p>
      ) : null}

      {error ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={error instanceof Error ? error.message : "That ledger could not be loaded."}
        />
      ) : null}

      <Card size="none" className="overflow-hidden">
        <div className="border-line text-fg flex items-center justify-between border-b px-3 py-2 text-sm">
          <span>
            <span className="font-medium">Opening balance</span>
            <span className="text-fg-muted"> · {formatDate(from)}</span>
          </span>
          <span className="font-medium">{data ? formatAmount(data.opening) : ""}</span>
        </div>
        <DataTable
          aria-label={data ? `General ledger for ${data.code} ${data.name}` : "General ledger"}
          size="sm"
          enableSorting={false}
          columns={COLUMNS}
          data={data?.lines ?? []}
          getRowId={(line, index) => `${line.journalId}-${index}`}
          loading={isPending}
          emptyContent={
            filtered
              ? "No lines match these filters."
              : "No transactions on this account in the period."
          }
          pagination={{ mode: "manual", show: false }}
          containerClassName="rounded-none border-0"
        />
        <div className="border-line text-fg flex items-center justify-between gap-6 border-t px-3 py-2 text-sm font-medium">
          <span>
            Closing balance<span className="text-fg-muted font-normal"> · {formatDate(to)}</span>
          </span>
          <span className="flex gap-6">
            <span>{data ? formatAmount(data.totalDebit) : ""}</span>
            <span>{data ? formatAmount(data.totalCredit) : ""}</span>
            <span className="font-semibold">{data ? formatAmount(data.closing) : ""}</span>
          </span>
        </div>
      </Card>

      {data && data.matching > data.lines.length ? (
        <p className="text-fg-muted text-xs">
          Showing the first {data.lines.length} of {data.matching} lines. Narrow the period to see
          the rest. The totals cover all of them.
        </p>
      ) : null}
    </ReportFrame>
  );
}
